import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI, deleteDataAPI } from '../../utils/fetchData';
import { useDispatch } from 'react-redux';
import { setSuccessAlert, setErrorAlert } from '../../store/alert/alertSlice';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useConfirmation } from '../../hooks/useConfirmation';

const FlowsApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Confirmation modal
  const { 
    modalState: confirmationState, 
    showConfirmation, 
    handleConfirm: handleConfirmAction, 
    closeConfirmation 
  } = useConfirmation();
  
  const [quickReminders, setQuickReminders] = useState([]);
  const [songReleaseWorkflows, setSongReleaseWorkflows] = useState([]);
  const [showWorkflows, setShowWorkflows] = useState([]);
  const [customWorkflows, setCustomWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('custom');
  const [selectedWorkflows, setSelectedWorkflows] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load all reminders and categorize them
  useEffect(() => {
    loadWorkflows();
  }, []);

  // Listen for changes
  useEffect(() => {
    const handleStorageChange = () => {
      loadWorkflows();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageUpdate', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdate', handleStorageChange);
    };
  }, []);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      
      // Load reminders from backend API
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/reminders', token);
      
      if (response?.status !== 200 && !response?.data?.success) {
        console.error('❌ Failed to load reminders:', response);
        throw new Error('Failed to load reminders');
      }
      
      const allReminders = response.data.data || [];
      
      console.log('🔍 FlowsApp - All reminders loaded:', allReminders.length);
      console.log('🔍 FlowsApp - Sample reminders:', allReminders.slice(0, 3).map(r => ({
        id: r._id,
        releaseId: r.releaseId,
        releaseName: r.releaseName,
        title: r.title,
        type: r.type,
        metadata: r.metadata
      })));
      
      // Categorize reminders into workflows
      const quick = [];
      const songReleases = {};
      const shows = {};
      const custom = {};
      
      allReminders.forEach(reminder => {
        // Map backend fields to frontend format
        const mappedReminder = {
          id: reminder._id,
          releaseId: reminder.releaseId,
          releaseName: reminder.releaseName,
          releaseDate: reminder.releaseDate,
          title: reminder.title,
          description: reminder.description,
          status: reminder.status,
          dueDate: reminder.dueDate,
          type: reminder.type,
          metadata: reminder.metadata || {}
        };
        
        console.log('🔍 Categorizing reminder:', {
          id: mappedReminder.id,
          releaseId: mappedReminder.releaseId,
          title: mappedReminder.title,
          type: mappedReminder.type,
          metadata: mappedReminder.metadata,
          quick_reminder: mappedReminder.metadata?.quick_reminder,
          custom: mappedReminder.metadata?.custom
        });

        // Check if it's a quick reminder (custom, standalone)
        if (mappedReminder.metadata?.quick_reminder) {
          console.log('📝 Adding to quick reminders');
          quick.push(mappedReminder);
        }
        // Check if it's a custom workflow (type === 'custom')
        else if (mappedReminder.type === 'custom') {
          console.log('🎨 Adding to custom workflows');
          const workflowId = mappedReminder.releaseId; // Use releaseId as the primary identifier
          if (!custom[workflowId]) {
            custom[workflowId] = {
              id: workflowId,
              name: mappedReminder.releaseName,
              releaseDate: mappedReminder.releaseDate,
              steps: [],
              completedSteps: 0,
              totalSteps: 0,
              isCustom: true
            };
          }
          custom[workflowId].steps.push(mappedReminder);
          custom[workflowId].totalSteps++;
          if (mappedReminder.status === 'completed') {
            custom[workflowId].completedSteps++;
          }
        }
        // Check if it's a show/booking workflow (type === 'booking' or metadata.type === 'booking')
        else if (mappedReminder.type === 'booking' || mappedReminder.metadata?.type === 'booking') {
          console.log('🎤 Adding to show workflows');
          const bookingId = mappedReminder.releaseId;
          if (!shows[bookingId]) {
            shows[bookingId] = {
              id: bookingId,
              name: mappedReminder.releaseName,
              showDate: mappedReminder.releaseDate,
              steps: [],
              completedSteps: 0,
              totalSteps: 0,
              isCustom: false
            };
          }
          shows[bookingId].steps.push(mappedReminder);
          shows[bookingId].totalSteps++;
          if (mappedReminder.status === 'completed') {
            shows[bookingId].completedSteps++;
          }
        }
        // Check if it's a song release workflow (type === 'release' or no type - default)
        else if (mappedReminder.type === 'release' || !mappedReminder.type) {
          console.log('🎵 Adding to song release workflows');
          const releaseId = mappedReminder.releaseId;
          if (!songReleases[releaseId]) {
            songReleases[releaseId] = {
              id: releaseId,
              name: mappedReminder.releaseName,
              releaseDate: mappedReminder.releaseDate,
              steps: [],
              completedSteps: 0,
              totalSteps: 0,
              isCustom: false
            };
          }
          songReleases[releaseId].steps.push(mappedReminder);
          songReleases[releaseId].totalSteps++;
          if (mappedReminder.status === 'completed') {
            songReleases[releaseId].completedSteps++;
          }
        } else {
          console.log('❓ Reminder not categorized:', mappedReminder);
        }
      });
      
      // Convert to arrays, filter out invalid dates, and sort
      const songReleaseArray = Object.values(songReleases)
        .filter(workflow => {
          const date = new Date(workflow.releaseDate);
          return !isNaN(date.getTime()) && workflow.releaseDate; // Only include valid dates
        })
        .sort((a, b) => new Date(a.releaseDate) - new Date(b.releaseDate))
        .slice(0, 5); // Show up to 5 upcoming releases
      
      const showsArray = Object.values(shows)
        .sort((a, b) => new Date(a.showDate) - new Date(b.showDate))
        .slice(0, 20); // Show up to 20 upcoming shows
      
      const customArray = Object.values(custom)
        .filter(workflow => {
          const date = new Date(workflow.releaseDate);
          return !isNaN(date.getTime()) && workflow.releaseDate; // Only include valid dates
        })
        .sort((a, b) => new Date(a.releaseDate) - new Date(b.releaseDate));
      
      // Sort quick reminders by due date
      const sortedQuick = quick.sort((a, b) => 
        new Date(a.dueDate) - new Date(b.dueDate)
      );
      
      setQuickReminders(sortedQuick);
      setSongReleaseWorkflows(songReleaseArray);
      setShowWorkflows(showsArray);
      setCustomWorkflows(customArray);
      
      console.log('📊 Workflows loaded:', {
        quickReminders: sortedQuick.length,
        songReleases: songReleaseArray.length,
        shows: showsArray.length,
        custom: customArray.length
      });

      console.log('🔍 Final categorized workflows:', {
        custom: Object.keys(custom),
        songReleases: Object.keys(songReleases),
        shows: Object.keys(shows)
      });
      
    } catch (error) {
      console.error('Error loading workflows:', error);
      dispatch(setErrorAlert('Failed to load workflows'));
    } finally {
      setLoading(false);
    }
  };

  const getCompletionPercentage = (workflow) => {
    if (workflow.totalSteps === 0) return 0;
    return Math.round((workflow.completedSteps / workflow.totalSteps) * 100);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDaysUntil = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `in ${diffDays} days`;
  };

  const handleWorkflowSelect = (workflow) => {
    setSelectedWorkflows(prev => {
      const isSelected = prev.some(item => item.id === workflow.id);
      if (isSelected) {
        return prev.filter(item => item.id !== workflow.id);
      } else {
        return [...prev, workflow];
      }
    });
  };

  const handleSelectAll = () => {
    const currentWorkflows = getCurrentWorkflows();
    const allSelected = currentWorkflows.every(workflow => 
      selectedWorkflows.some(item => item.id === workflow.id)
    );
    
    if (allSelected) {
      // Deselect all current workflows
      setSelectedWorkflows(prev => 
        prev.filter(item => !currentWorkflows.some(workflow => workflow.id === item.id))
      );
    } else {
      // Select all current workflows
      const newSelections = currentWorkflows.filter(workflow => 
        !selectedWorkflows.some(item => item.id === workflow.id)
      );
      setSelectedWorkflows(prev => [...prev, ...newSelections]);
    }
  };

  const handleBulkDelete = () => {
    if (selectedWorkflows.length === 0) return;

    showConfirmation({
      title: 'Delete Workflows',
      message: `Are you sure you want to delete ${selectedWorkflows.length} selected workflow(s)? This will remove all associated reminders.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonColor: 'bg-[#FF3B30]',
      onConfirm: () => performBulkDelete()
    });
  };

  const performBulkDelete = async () => {
    setIsDeleting(true);
    const token = JSON.parse(localStorage.getItem('dizeeToken'));
    let successCount = 0;
    let failCount = 0;

    for (const workflow of selectedWorkflows) {
      try {
        // Delete all reminders in the workflow
        const deletePromises = workflow.steps.map(step => 
          deleteDataAPI(`user/reminders/${step.id}`, token)
        );
        
        await Promise.all(deletePromises);
        successCount++;
      } catch (error) {
        console.error('Error deleting workflow:', workflow.name, error);
        failCount++;
      }
    }

    if (successCount > 0) {
      const message = `Successfully deleted ${successCount} workflow(s)${failCount > 0 ? `, ${failCount} failed` : ''}`;
      dispatch(setSuccessAlert(message));
      setSelectedWorkflows([]);
      loadWorkflows(); // Reload workflows
    } else {
      dispatch(setErrorAlert('Failed to delete workflows'));
    }

    setIsDeleting(false);
  };

  const getCurrentWorkflows = () => {
    switch (activeTab) {
      case 'custom':
        return customWorkflows;
      case 'song_release':
        return songReleaseWorkflows;
      case 'upcoming_show':
        return showWorkflows;
      default:
        return [];
    }
  };

  const getCreateRoute = () => {
    switch (activeTab) {
      case 'custom':
        return '/flows/create-workflow';
      case 'song_release':
        return '/flows/song-release';
      case 'upcoming_show':
        return '/flows/upcoming-show';
      default:
        return '/flows/create';
    }
  };

  const renderWorkflow = (workflow) => {
    const isComplete = getCompletionPercentage(workflow) === 100;
    const isSelected = selectedWorkflows.some(item => item.id === workflow.id);
    
    return (
      <div
        key={workflow.id}
        className={`w-full bg-black border rounded-lg p-4 transition-all relative cursor-pointer ${
          isSelected 
            ? 'border-[#FF3B30] bg-[rgba(255,59,48,0.1)]' 
            : 'border-[rgba(255,255,255,0.1)] hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)]'
        }`}
        onClick={() => handleWorkflowSelect(workflow)}
      >
        <div className="w-full text-left">
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 flex-wrap flex-1 pr-2">
                <p className="text-white text-[12px] font-normal">{workflow.name}</p>
                {workflow.isCustom && (
                  <span className="text-[#FF3B30] text-[8px] px-2 py-0.5 bg-[rgba(255,59,48,0.1)] border border-[rgba(255,59,48,0.3)] rounded-full">
                    CUSTOM
                  </span>
                )}
                {isComplete && (
                  <span className="text-green-500 text-[8px] px-2 py-0.5 bg-[rgba(34,197,94,0.1)] border border-[rgba(34,197,94,0.3)] rounded-full">
                    COMPLETE
                  </span>
                )}
              </div>
              
              {/* View button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/flows/workflow/${workflow.id}`);
                }}
                className="text-[#FF3B30] text-[10px] px-2 py-1 hover:bg-[rgba(255,59,48,0.1)] rounded transition-colors"
              >
                View
              </button>
            </div>
              <p className="text-[rgba(255,255,255,0.7)] text-[10px]">
                {activeTab === 'upcoming_show' ? 'Show: ' : 'Releasing '}
                {formatDate(workflow.releaseDate || workflow.showDate)} • {getDaysUntil(workflow.releaseDate || workflow.showDate)}
              </p>
          </div>
          <div className="flex items-end justify-between mb-1">
            <div></div>
            <p className={`text-[11px] font-normal ${isComplete ? 'text-green-500' : 'text-[#FF3B30]'}`}>
              {getCompletionPercentage(workflow)}%
            </p>
          </div>
          <div className="w-full bg-[rgba(255,255,255,0.1)] rounded-full h-1.5">
            <div 
              className={`h-1.5 rounded-full transition-all ${isComplete ? 'bg-green-500' : 'bg-[#FF3B30]'}`}
              style={{ width: `${getCompletionPercentage(workflow)}%` }}
            />
          </div>
        </div>
      </div>
    );
  };

  // Debug function - can be called from browser console
  window.debugFlowsData = () => {
    const reminders = JSON.parse(localStorage.getItem('dizee_reminders') || '[]');
    console.log('🔍 DEBUG: All reminders in localStorage:', reminders.length);
    console.log('🔍 DEBUG: Sample reminders:', reminders.slice(0, 5).map(r => ({
      id: r.id,
      releaseId: r.releaseId,
      releaseName: r.releaseName,
      title: r.title,
      metadata: r.metadata
    })));
    
    const custom = reminders.filter(r => r.metadata?.custom === true);
    const booking = reminders.filter(r => r.metadata?.type === 'booking');
    const release = reminders.filter(r => r.metadata?.type === 'release' || !r.metadata?.type);
    
    console.log('🔍 DEBUG: Categorization:', {
      custom: custom.length,
      booking: booking.length,
      release: release.length
    });
    
    console.log('🔍 DEBUG: Custom reminders details:', custom.map(r => ({
      id: r.id,
      releaseId: r.releaseId,
      releaseName: r.releaseName,
      releaseDate: r.releaseDate,
      title: r.title,
      templateId: r.templateId,
      metadata: r.metadata
    })));
    
    return { reminders, custom, booking, release };
  };

  // Debug function for browser console
  window.debugFlowsData = () => {
    console.log('🔍 Current workflows state:');
    console.log('Custom workflows:', customWorkflows);
    console.log('Song release workflows:', songReleaseWorkflows);
    console.log('Show workflows:', showWorkflows);
    console.log('Quick reminders:', quickReminders);
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Workflows</span>
            <div className="flex items-center gap-4">
              {selectedWorkflows.length > 0 ? (
                <>
                  <span className="text-white text-[12px] font-normal">
                    Selected ({selectedWorkflows.length})
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    disabled={isDeleting}
                    className="text-red-500 text-[12px] font-normal cursor-pointer hover:opacity-70 disabled:opacity-50"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                </>
              ) : (
                <>
                  <span 
                    className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                    onClick={() => navigate('/flows/create')}
                  >
                    Create
                  </span>
                  
                  <span 
                    className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                    onClick={() => navigate(-1)}
                  >
                    Go back
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="w-full mb-6">
            <div className="flex px-4">
              {[
                { id: 'custom', label: 'Custom' },
                { id: 'song_release', label: 'Song Release' },
                { id: 'upcoming_show', label: 'New Show' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSelectedWorkflows([]); // Clear selections when switching tabs
                  }}
                  className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#FF3B30]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="w-full px-4 flex-1 overflow-y-auto">
            {loading ? (
              /* Loading State */
              <div className="text-left py-[60px]">
                <div style={{ 
                  color: 'rgba(255, 255, 255, 0.5)', 
                  fontSize: '12px', 
                  fontWeight: '600',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Loading your workflows
                  <span className="loading-dots" style={{
                    marginLeft: '2px'
                  }}></span>
                </div>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '12px',
                  lineHeight: '1.5',
                  marginBottom: '24px',
                  maxWidth: '300px'
                }}>
                  {activeTab === 'custom' ? 'Fetching your custom workflows and reminders...' : 
                   activeTab === 'song_release' ? 'Loading your upcoming song releases...' :
                   'Getting your show workflows ready...'}
                </p>
                <style>{`
                  @keyframes thinking {
                    0% { content: ''; }
                    33% { content: '.'; }
                    66% { content: '..'; }
                    100% { content: '...'; }
                  }
                  .loading-dots::after {
                    content: '';
                    animation: thinking 1.2s infinite;
                  }
                `}</style>
              </div>
            ) : getCurrentWorkflows().length === 0 ? (
              /* Empty State */
              <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-normal mb-2">
                  No {activeTab === 'custom' ? 'custom' : activeTab === 'song_release' ? 'song release' : 'show'} workflows yet
                </h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                  Create your first {activeTab === 'custom' ? 'custom' : activeTab === 'song_release' ? 'song release' : 'show'} workflow to get started.
                </p>
                <button 
                  onClick={() => navigate(getCreateRoute())}
                  className="bg-[#FF3B30] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[rgba(255,59,48,0.9)] transition-colors"
                >
                  New Workflow
                </button>
              </div>
            ) : (
              <>
                {/* Select All Button */}
                {getCurrentWorkflows().length > 0 && (
                  <div className="px-4 mb-4 text-right">
                    <button
                      onClick={handleSelectAll}
                      className="text-[#FF3B30] text-[10px] hover:text-[rgba(255,59,48,0.8)] transition-colors"
                    >
                      {getCurrentWorkflows().every(workflow => 
                        selectedWorkflows.some(item => item.id === workflow.id)
                      ) ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                )}

                {/* Workflows List */}
                <div className="grid gap-4">
                  {getCurrentWorkflows().map(renderWorkflow)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onClose={closeConfirmation}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        confirmButtonColor={confirmationState.confirmButtonColor}
        onConfirm={handleConfirmAction}
      />
    </LayoutHeader>
  );
};

export default FlowsApp;


